import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import './Login.css'

const logo = process.env.PUBLIC_URL + '/images/kbl_logo.png'
const PUBLIC_URL = process.env.PUBLIC_URL || ''

const COMPANY_IMAGES = [
  `${PUBLIC_URL}/images/image1.jpeg`,
  `${PUBLIC_URL}/images/image2.avif`,
  `${PUBLIC_URL}/images/image3.avif`,
  `${PUBLIC_URL}/images/image4.avif`,
  `${PUBLIC_URL}/images/image5.avif`,
  `${PUBLIC_URL}/images/image6.avif`,
]

const CAPTIONS = [
  'Bunker survey operations worldwide',
  'Professional marine quantity inspection',
  'Global port & vessel coverage',
  'Accurate fuel recovery reporting',
  'Trusted by leading shipping clients',
  'KBL Marine bunker survey specialists',
]

const SERVICES = [
  { icon: 'bi-fuel-pump', title: 'Bunker Surveys' },
  { icon: 'bi-globe2', title: 'Global Coverage' },
  { icon: 'bi-graph-up-arrow', title: 'Fuel Recovery' },
  { icon: 'bi-calendar3', title: 'Operations' },
  { icon: 'bi-receipt', title: 'Invoicing' },
  { icon: 'bi-shield-check', title: 'Client Portal' },
]

const Login = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [slideIndex, setSlideIndex] = useState(0)
  const [imagesLoaded, setImagesLoaded] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    let loaded = 0
    let anySuccess = false
    COMPANY_IMAGES.forEach((src) => {
      const img = new Image()
      img.onload = () => {
        anySuccess = true
        loaded += 1
        if (loaded === COMPANY_IMAGES.length) setImagesLoaded(anySuccess)
      }
      img.onerror = () => {
        loaded += 1
        if (loaded === COMPANY_IMAGES.length) setImagesLoaded(anySuccess)
      }
      img.src = src
    })
  }, [])

  // Restarts on every slide change so a manual pick gets the full 5s, matching the progress bar
  useEffect(() => {
    if (!imagesLoaded) return
    const timer = setTimeout(() => {
      setSlideIndex((i) => (i + 1) % COMPANY_IMAGES.length)
    }, 5000)
    return () => clearTimeout(timer)
  }, [imagesLoaded, slideIndex])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const result = await login(username, password)

    if (result.success) {
      navigate('/')
    } else {
      setError(result.error || 'Login failed')
    }

    setLoading(false)
  }

  return (
    <div className="login-kbl">
      <div className="login-kbl-orb login-kbl-orb-1" aria-hidden="true" />
      <div className="login-kbl-orb login-kbl-orb-2" aria-hidden="true" />

      <aside className="login-kbl-showcase" aria-label="About KBL Marine">
        <div className="login-kbl-media">
          {imagesLoaded ? (
            COMPANY_IMAGES.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                className={`login-kbl-media-img ${i === slideIndex ? 'active' : ''}`}
              />
            ))
          ) : (
            <div className="login-kbl-media-fallback" />
          )}
          <div className="login-kbl-media-shade" />
        </div>

        <div className="login-kbl-showcase-inner">
          <a
            className="login-kbl-site"
            href="https://www.kblsurvey.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.kblsurvey.com
            <i className="bi bi-arrow-up-right" />
          </a>

          <div className="login-kbl-story">
            <p className="login-kbl-eyebrow">Marine Survey Management</p>
            <h1 className="login-kbl-headline">
              Welcome aboard
              <span>KBL Marine</span>
            </h1>
            <p className="login-kbl-tagline">
              Bunker surveys, fuel recovery and operations — accurate, worldwide.
            </p>

            {imagesLoaded && (
              <div className="login-kbl-slides">
                <p className="login-kbl-caption" aria-live="polite">
                  {CAPTIONS[slideIndex]}
                </p>
                <div className="login-kbl-progress">
                  {COMPANY_IMAGES.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`login-kbl-progress-seg ${i === slideIndex ? 'active' : ''} ${i < slideIndex ? 'done' : ''}`}
                      onClick={() => setSlideIndex(i)}
                      aria-label={`Slide ${i + 1}`}
                    >
                      <span />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <ul className="login-kbl-services">
              {SERVICES.map((s) => (
                <li key={s.title} className="login-kbl-service">
                  <i className={`bi ${s.icon}`} />
                  {s.title}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </aside>

      <main className="login-kbl-form-panel">
        <div className="login-kbl-form-card">
          <div className="login-kbl-form-brand">
            <img src={logo} alt="KBL Marine Surveys" className="login-kbl-form-logo" />
          </div>

          <div className="login-kbl-form-heading">
            <h2 className="login-kbl-form-welcome">Welcome back</h2>
            <p className="login-kbl-form-sub">Sign in to your survey workspace</p>
          </div>

          {error && (
            <div className="login-kbl-error" role="alert">
              <i className="bi bi-exclamation-circle-fill" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="login-kbl-form-fields">
              <div>
                <label htmlFor="username" className="login-kbl-label">Username</label>
                <div className="login-kbl-input-wrap">
                  <i className="bi bi-person login-kbl-input-icon" />
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="login-kbl-input"
                    placeholder="Enter your username"
                    required
                    autoFocus
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="login-kbl-label">Password</label>
                <div className="login-kbl-input-wrap">
                  <i className="bi bi-lock login-kbl-input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-kbl-input has-toggle"
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-kbl-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
              </div>

              <button type="submit" className="login-kbl-submit" disabled={loading}>
                <span className="login-kbl-submit-inner">
                  {loading ? (
                    <>
                      <span className="login-kbl-spinner" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-box-arrow-in-right" />
                      Sign In
                    </>
                  )}
                </span>
              </button>
            </div>
          </form>

          <footer className="login-kbl-form-footer">
            <a href="/public-password-change" className="login-kbl-form-link">
              <i className="bi bi-key" />
              Change Password
            </a>
            <span className="login-kbl-form-copy">
              © {new Date().getFullYear()} KBL Marine
            </span>
          </footer>
        </div>
      </main>
    </div>
  )
}

export default Login
